import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from './AuthContext';
import logger from '../utils/logger';

// Single source of truth for user settings (currently unitSystem). Previously
// every view fetched the user doc separately on mount; this context holds one
// real-time listener and shares the value.

export type UnitSystem = 'Metric' | 'US' | 'Imperial';

const UNIT_SYSTEM_STORAGE_KEY = 'settings_unit_system';

interface UserSettingsContextType {
  unitSystem: UnitSystem;
}

const UserSettingsContext = createContext<UserSettingsContextType | undefined>(undefined);

export const UserSettingsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [unitSystem, setUnitSystem] = useState<UnitSystem>(() => {
    try {
      const cached = localStorage.getItem(UNIT_SYSTEM_STORAGE_KEY);
      if (cached === 'Metric' || cached === 'US' || cached === 'Imperial') return cached;
    } catch {}
    return 'Metric';
  });

  useEffect(() => {
    if (!user?.uid) return;

    const unsubscribe = onSnapshot(
      doc(db, 'users', user.uid),
      (snapshot) => {
        const value = snapshot.data()?.unitSystem;
        if (value === 'Metric' || value === 'US' || value === 'Imperial') {
          setUnitSystem(value);
          try {
            localStorage.setItem(UNIT_SYSTEM_STORAGE_KEY, value);
          } catch {}
        }
      },
      (error) => {
        logger.error('UserSettings listener error:', error);
      }
    );

    return () => unsubscribe();
  }, [user?.uid]);

  return (
    <UserSettingsContext.Provider value={{ unitSystem }}>
      {children}
    </UserSettingsContext.Provider>
  );
};

export const useUserSettings = (): UserSettingsContextType => {
  const context = useContext(UserSettingsContext);
  if (context === undefined) {
    throw new Error('useUserSettings must be used within a UserSettingsProvider');
  }
  return context;
};
